// ABOUTME: Cavalry WASM player for the voice visualizer overlay.
// Uses module-level singletons for Module, player, and surface so React
// StrictMode double-mounting does not create two conflicting WebGL contexts.
// Voice state drives the three Control Centre blend sliders.
// NOTE: Art team must set comp background to transparent in Cavalry.

import { useEffect, useRef, useState } from 'react';

const SCENE_PATH = '/Voice_Animation.cv';

// Voice_Animation.cv (2026-08-28 rig, comp "Agent V011 Web Ready") replaces the
// 2026-08-20 "Clean up V002_Master" export. Node IDs are NOT stable across Cavalry
// exports — every re-export renumbers them — so these were re-read straight from
// the .cv JSON (`nodes[].attributes.niceName.value`) rather than assumed to carry
// over. Every id below moved in this export; nothing carried across.
//
// The rig is unchanged in kind: INDEPENDENT named blend shapes, each blending 0->1
// on its own, all wired as deformers on the one visible "Idle" duplicator, so
// all-zero == the neutral idle cluster exactly as before. V011 grew the set from
// 5 to 8 — "Error", "Share" and "Idle - Waiting" were new.
//
// Those three were still listed here ON PURPOSE rather than left out, even while
// nothing drove them. An unmapped shape is never written, so it keeps whatever the
// artist left in the file — and V011 ships with Speaking authored at blend=100
// (slider left up at export time), which is exactly how a stuck shape gets into a
// build unnoticed. Naming all eight pins every one of them to its lerped value from
// frame one. "Error" and "Share" are still in that position: named, held at 0. Wire
// a call site to one of those key names and it starts blending with no change here.
//
// `idleWaiting` IS driven as of 2026-09-01 — it is the resting state, reached via the
// STATE_ALIASES entry below. The key keeps its scene-faithful name rather than being
// renamed 'idle': the alias is one line, and NODES is asserted key-by-key against the
// shipped .cv by CavalryViz.scene.test.js, so its keys should read as the scene's
// own vocabulary and not the app's.
//
// None of these `.blend` attributes has an inbound connection/keyframe in the file
// (verified against `connections` for all 8 — only `.inputShape`/deformer wiring
// appears there), so driving them with setAttribute() every tick is safe — tick()
// will not fight the manual value.
// Exported for CavalryViz.scene.test.js, which reads the shipped public/Voice_Animation.cv and
// asserts every id below still resolves to the shape its key claims. That test is the whole reason
// a re-export cannot land silently again: nothing else in the app fails loudly when an id goes
// stale — the visualizer just stops moving.
export const NODES = {
  speaking:    'blendSubMeshPositions#87', // anyone talking — agent or guest
  // Authored and wired, but nothing reaches it since 2026-09-01: 'speaking' covers both speakers.
  // Still named here ON PURPOSE so it is pinned to 0 every frame rather than left wherever the
  // artist's slider was at export time — see the note above.
  listening:   'blendSubMeshPositions#88',
  thinking:    'blendSubMeshPositions#82',
  cart:        'blendSubMeshPositions#84', // niceName "Bag"
  wishlist:    'blendSubMeshPositions#85', // niceName "Heart"
  idleWaiting: 'blendSubMeshPositions#95', // niceName "Idle - Waiting" — the resting state
  // Driven since 2026-09-01: the thinking watchdog's silence beat
  // (state/thinkingSilenceBeat.js) flashes it via the reaction channel before the
  // cluster rests; any live voice outranks it (see voiceVisualizerState precedence).
  error:       'blendSubMeshPositions#86', // niceName "Error"
  // Authored in V011, still not driven by any app state — held at 0.
  share:       'blendSubMeshPositions#96', // niceName "Share"
};
const ATTR = 'blend';
const STATE_KEYS = Object.keys(NODES);

// `resolveVisualizerState` returns 'idle' for "nobody is talking" (2026-09-01). The scene calls
// that shape "Idle - Waiting", and the NODES key deliberately does NOT use the bare word 'idle'
// (see the note above), so the two are bridged here rather than by renaming either side.
// Exported so CavalryViz.scene.test.js can assert the bridge itself, not just that both ends
// happen to exist — renaming one side only is the easy mistake and it degrades silently.
export const STATE_ALIASES = { idle: 'idleWaiting' };

// Where an unrecognised state lands. This used to be 'listening', from when nothing ever returned
// 'idle' and the cluster's resting pose WAS the listening shape. Now that a real resting state
// exists, falling back to it is both more honest and more robust — an unknown string parks the
// cluster at rest rather than on a shape that claims someone is talking.
export const FALLBACK_KEY = 'idleWaiting';

const LERP_SPEED = 0.06;

// ─── Voice-reactive envelope ──────────────────────────────────────────────────
// Two of the states are driven by live audio rather than a flat on/off, so they need their own
// timing: a quick attack so a syllable registers as a visible push, and a slower release so the
// cluster settles instead of strobing between words. LERP_SPEED (0.06) is a ~1s ease, which is
// right for a state *change* but far too slow to track speech.
// Attack was 0.35 until 2026-09-01 — softened on feedback that the reaction to a voice was too
// aggressive. Release is unchanged; it was never the loud part.
const VOICE_ATTACK  = 0.22;
const VOICE_RELEASE = 0.12;

// The mic/agent RMS never reaches 1.0 in normal speech, so mapping it straight onto blend 0-1
// would only ever use the bottom of the shape's range and the cluster would barely move.
const VOICE_GAIN = 2.2;

// Map the usable part of the signal onto a floor..1 blend: the floor keeps the shape legible
// during quiet passages rather than collapsing back to the neutral pose mid-sentence. Raising a
// floor also NARROWS the quiet->loud travel, which is the least destructive way to calm a state
// down — it softens the reaction without flattening the shape itself. Speaking's floor went
// 0.35 -> 0.5 on the 2026-09-01 "too intense" feedback.
const SPEAK_FLOOR = 0.5;

// ─── Live motion ──────────────────────────────────────────────────────────────
// The blend attributes only choose WHICH shape the cluster morphs into — they carry no motion of
// their own. Every shape's movement comes from an oscillator/noise living INSIDE that shape's
// hidden target duplicator, and it reaches the screen multiplied by that shape's `.blend`. That
// is the single most important fact about this rig, and the one that is easiest to get wrong:
//
//   oscillator#21 -> duplicator#89.shapeScale -> blendSubMeshPositions#87.inputShape -> visible
//                    (hidden "Speaking")         (.blend scales how much of it lands)
//
// So driving `oscillator#21` while the Speaking blend has lerped to 0 writes attributes that
// nothing on screen reads. Each reactive state therefore has to drive ITS OWN oscillator; there
// is no shared one, and the speaking pulse cannot be reused to animate listening.
//
// PULSE_NODE survived the V011 re-export as the same node (uuid 5b8f6523-…, renumbered from the
// V002 `oscillator#18`), so its tuning below is inherited, not guessed.
export const PULSE_NODE = 'oscillator#21';    // inside the hidden "Speaking" target
//
// If the Listening shape is ever driven again (it is authored and wired, just not currently
// reached — see voiceVisualizerState.js), its reactive node is `simplexNoise#7`, and the two
// attributes to write are `minimum` (magnitude, authored 111) and `timeScale` (pace, authored
// 1.37). NOT `oscillator#23`, the other deformer stacked on the same `basicShape#33`: it runs at
// ±16 against the noise's ±111 on a polygon of radius 380, so driving it works out sub-pixel at
// footer size — wired, but invisible. NOT `maximum` on either, which is connection-driven from
// `minimum` with a `*-1` expression and would be discarded by tick().

// AGENT SPEAKING — amplitude -> how far the dots throw on each pulse. The QUIET end is the value
// the artist authored, so a silent moment looks exactly like the rig as delivered, and loud speech
// overshoots it. V011 raised that authored maximum 1.1 -> 1.2 (scale range 0.5->1.2).
// The loud end has come down twice on JC's eye: 1.9 -> 1.45 (2026-08-28), then 1.45 -> 1.32
// (2026-09-01, "too intensive") — halving the remaining travel. At footer size the cluster only
// needs to hint at the voice, not perform it. Note this is now the GUEST's voice as much as the
// agent's, and the "too intensive" report was about the guest's, so this is the number that
// feedback lands on. Widen the gap between these two, not VOICE_GAIN, if it ever needs more: gain
// also feeds the blend morph, so raising it changes shape as well as scale.
export const PULSE_MAX_QUIET = 1.2;    // the authored value (V011)
const PULSE_MAX_LOUD  = 1.32;

// Amplitude -> how fast it pulses. Cavalry evaluates the sine from the current time, so a large
// frequency jump between frames shifts its phase and shows up as a visible stutter. Keep the span
// modest and let VOICE_SMOOTHING ease it; intensity is doing most of the expressive work.
// Loud end 0.28 -> 0.24 (2026-09-01), halved alongside the throw.
export const PULSE_FREQ_QUIET = 0.2;   // the authored value (unchanged in V011)
const PULSE_FREQ_LOUD  = 0.24;

// Applied to the amplitude signal feeding the oscillators (not to the blend, which has its own
// attack/release). RMS is jittery frame to frame; without this the cluster buzzes. Lowered
// 0.25 -> 0.16 on 2026-09-01: a slower tracker is a calmer cluster, and it was the frame-to-frame
// jitter as much as the travel that read as "too intense".
const VOICE_SMOOTHING = 0.16;

// The audio-reactive states, each bound to the oscillator that lives inside its own target.
// `quiet` is always the artist's authored value, so a silent moment is the rig exactly as
// delivered and only speech pushes it off that.
//
// Keyed by state rather than inlined even though there is only one entry today: the per-state
// binding is the whole point (an oscillator is inert unless ITS shape is blended in), and a second
// entry is the entire cost of making another state reactive. See the Listening note above PULSE_NODE.
const REACTIVE = {
  speaking: {
    floor: SPEAK_FLOOR,
    node:  PULSE_NODE,
    attrs: [
      { name: 'maximum',   quiet: PULSE_MAX_QUIET,  loud: PULSE_MAX_LOUD },
      { name: 'frequency', quiet: PULSE_FREQ_QUIET, loud: PULSE_FREQ_LOUD },
    ],
  },
};
const REACTIVE_KEYS = Object.keys(REACTIVE);

// ─── Singletons ───────────────────────────────────────────────────────────────
// Only the WASM module and the parsed scene (`player`) are safe to share across
// mounts — both are expensive and hold no per-canvas state. The WebGL *surface*
// is NOT shared: it is a context bound to one specific <canvas> DOM element, so
// every mount must bind its own, even when reusing the same player. Sharing the
// surface (the old design) meant a second CavalryViz — a StrictMode remount, a
// mobile/desktop composition swap, or two call sites — kept rendering into
// whichever canvas happened to load the scene first, possibly one no longer in
// the DOM, while the canvas actually on screen never received a single pixel.

let modulePromise = null;
let playerPromise = null;
let sharedDims    = null;

export function getCavalryModule() {
  if (!modulePromise) {
    modulePromise = import(/* webpackIgnore: true */ '/wasm-lib/CavalryWasm.js')
      .then(wasm => wasm.default({
        locateFile: (path) => `/wasm-lib/${path}`,
        print:    (t) => console.log('[cavalry]', t),
        printErr: (t) => console.error('[cavalry]', t),
      }))
      // A cached REJECTION is permanent: every later mount awaits the same failed promise and
      // fails identically, so one bad fetch kills the visualizer for the whole session. Drop the
      // singleton on failure so the next caller genuinely retries.
      .catch((err) => { modulePromise = null; throw err; });
  }
  return modulePromise;
}

// Warm the WASM module (12.6 MB fetch + compile) while the guest is still reading the gate
// screen, so it never competes with the intro stream + next-chapter preload after Enter.
// Idempotent by construction — the singleton promise above is all it touches.
export function warmCavalryModule() {
  try { getCavalryModule().catch(() => {}); } catch (_) { /* warmup must never throw */ }
}

/**
 * Takes the Module's pending-asset queue AND resets it.
 *
 * `pendingAssets` lives on the Emscripten Module, and there is one Module for the whole app —
 * CavalryViz and CavalryLoaderMark both go through getCavalryModule(). Neither used to clear the
 * queue after consuming it, so whichever scene loaded second replayed the first scene's assets
 * against its own player. Draining is the fix whichever way the API behaves: if MakeWithPath
 * repopulates the queue per scene, resetting is a no-op; if it appends, resetting is the bug fix.
 */
export function drainPendingAssets(Module) {
  const pending = Module?.pendingAssets ?? [];
  if (Module) Module.pendingAssets = [];
  return pending;
}

// Loads and parses the scene exactly once; every mount awaits the same promise.
function getPlayer() {
  if (!playerPromise) {
    playerPromise = (async () => {
      const Module = await getCavalryModule();

      const res = await fetch(SCENE_PATH);
      if (!res.ok) throw new Error(`Scene fetch failed: ${res.status}`);
      const bytes = new Uint8Array(await res.arrayBuffer());

      try { Module.FS.unlink('scene.cv'); } catch (_) {}
      Module.FS.writeFile('scene.cv', bytes);

      const player = Module.Cavalry.MakeWithPath('scene.cv');

      const pendingAssets = drainPendingAssets(Module);
      if (pendingAssets.length) {
        await Promise.all(pendingAssets.map(async (asset) => {
          const r = await fetch(`/${asset.filename}`);
          if (!r.ok) { console.warn('[cavalry] asset not found:', asset.filename, r.status); return; }
          const buf = new Uint8Array(await r.arrayBuffer());
          Module.FS.writeFile(asset.filename, buf);
          if (asset.type === 'image') player.replaceImageAsset(asset.filename, asset.assetId);
          else if (asset.type === 'font') { Module.loadFont(asset.filename); player.replaceFontAsset(asset.filename, asset.assetId); }
        }));
      }

      const { width, height } = player.getSceneResolution();
      sharedDims = { w: width, h: height };
      return { Module, player };
    })()
      // Same reasoning as getCavalryModule: never let one failure become the permanent answer.
      .catch((err) => { playerPromise = null; throw err; });
  }
  return playerPromise;
}

/**
 * Tears a bound surface back down. THIS IS NOT OPTIONAL BOOKKEEPING — it is what keeps two
 * Cavalry players from corrupting each other.
 *
 * Every `makeWebGLSurfaceFromElement` creates a WebGL context and makes it current on the shared
 * Emscripten Module. There is one Module for the whole app (CavalryViz and CavalryLoaderMark both
 * go through getCavalryModule), and Cavalry allocates a scene's GPU objects against whichever
 * context is current when the scene is built. So a context left alive after its canvas is gone
 * silently becomes the context a LATER scene is built under — and that scene then draws into its
 * own, different context, where its shaders and buffers do not exist:
 *
 *   WebGL: INVALID_OPERATION: delete: object does not belong to this context
 *   WebGL: INVALID_OPERATION: drawElements: no valid shader program in use
 *
 * which presents as the visualizer simply vanishing. Losing the context explicitly is the only
 * portable way to free it — dropping the canvas reference just waits on GC, which is exactly the
 * nondeterminism that made this show up on some entries and not others.
 *
 * Verified 2026-08-25: releasing the boot loadermark's context here ALSO fixed an intermittent
 * freeze/jitter in the reel playback. The orphaned context was still live on the GPU and
 * contending with the chapter video's decode. If reel playback ever starts stuttering again,
 * count the live WebGL contexts before blaming the player or the encode — a leaked one does not
 * look like a video bug.
 */
export function releaseSurface({ Module, player, canvas, surface, targetKey }) {
  try { player?.stop?.(); } catch (_) { /* already stopped */ }
  try { surface?.delete?.(); } catch (_) { /* embind object may already be freed */ }
  try { if (Module?.specialHTMLTargets) delete Module.specialHTMLTargets[targetKey]; } catch (_) {}
  try {
    const gl = canvas?.getContext?.('webgl2') ?? canvas?.getContext?.('webgl');
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch (_) { /* context already gone */ }
}

// Binds a fresh WebGL surface to THIS mount's canvas. Must run on every mount,
// even when `player` is a reused shared instance.
function bindSurface(Module, player, canvas, dims) {
  canvas.id = 'canvas';
  Module.specialHTMLTargets = Module.specialHTMLTargets ?? {};
  Module.specialHTMLTargets['#canvas'] = canvas;

  canvas.width  = 0;
  canvas.height = 0;
  canvas.width  = dims.w;
  canvas.height = dims.h;

  const surface = Module.makeWebGLSurfaceFromElement(canvas, dims.w, dims.h);
  player.render(surface);
  if (!player.isPlaying()) player.play();
  return surface;
}

// ─── Component ────────────────────────────────────────────────────────────────

// `amplitudeRef` is an optional ref holding the current 0-1 voice level (App.js's `currentVolume`,
// the same signal the legacy Visualizer bars use). Pass it to get an audio-reactive speaking state;
// omit it and speaking falls back to a flat on/off blend over the authored loop.
//
// One signal is correct here rather than a per-speaker pair: `currentVolume` is max(agent, mic),
// and 'speaking' means whoever is talking, so the max IS that speaker. A per-speaker split was
// briefly added when the states named a speaker, and removed with them.
export default function CavalryViz({
  state = 'idle',
  maxWidth = 320,
  blendMode = 'multiply',
  filter = 'none',
  amplitudeRef = null,
}) {
  const canvasRef = useRef(null);
  const stateRef  = useRef(state);
  // The render loop is set up once (deps []), so it must not close over the prop directly — a call
  // site that swaps in a different ref object later would otherwise keep feeding the old one.
  const ampRef = useRef(amplitudeRef);
  const [error, setError] = useState(null);
  const [dims,  setDims]  = useState(sharedDims);

  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { ampRef.current = amplitudeRef; }, [amplitudeRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let rafId      = null;
    let aborted    = false;
    let retryTimer = null;
    // Captured so the cleanup can hand them to releaseSurface — without this the context this
    // mount created outlives the mount and poisons the next scene built on the shared Module.
    let bound      = null; // { Module, player, surface }

    function startLoop(player, surface) {
      // Cavalry's `blend` attribute is a 0–100 percentage, not a 0–1 float
      // (same convention as its 0–255 colours). Lerping 0→1 was a ~1% change,
      // so the shape never visibly morphed. Read each attribute's real numeric
      // range from the scene and map the normalized 0–1 lerp onto it.
      const ranges = {};
      STATE_KEYS.forEach((key) => {
        const id = NODES[key];
        let min = 0, max = 100;
        try {
          // Prefer hard bounds, then soft, else the 0–100 default (mirrors the
          // Cavalry SDK's own slider logic). Guard NaN from a malformed range.
          const ni = player.getAttributeDefinition(id, ATTR)?.numericInfo;
          if (ni) {
            if (ni.hasHardMin && Number.isFinite(ni.hardMin)) min = ni.hardMin;
            else if (ni.hasSoftMin && Number.isFinite(ni.softMin)) min = ni.softMin;
            if (ni.hasHardMax && Number.isFinite(ni.hardMax)) max = ni.hardMax;
            else if (ni.hasSoftMax && Number.isFinite(ni.softMax)) max = ni.softMax;
          }
          // If `blend` is driven by a connection/keyframe, player.tick() will
          // re-evaluate and overwrite our setAttribute every frame — the shape
          // would sit dead. Surface that instead of failing silently.
          if (player.getInConnection?.(id, ATTR)) {
            console.warn(`[CavalryViz] ${id}.${ATTR} is driven by a connection; tick() may override manual blend.`);
          }
        } catch (e) {
          // Most likely a wrong/renamed node id — without this warning it would
          // only manifest as setAttribute throwing 60×/s inside the tick catch.
          console.warn(`[CavalryViz] no attribute definition for ${id}.${ATTR}; defaulting range 0–100.`, e);
        }
        if (max === min) console.warn(`[CavalryViz] ${id}.${ATTR} has a zero-width range; blend will not move.`);
        ranges[key] = { min, max };
      });

      // One normalized (0–1) value per named shape, lerped toward 1 when it is
      // the current voice state's shape, else toward 0. Independent — no chain.
      const blends = {};
      STATE_KEYS.forEach((key) => { blends[key] = 0; });

      // Shapes whose setAttribute has already thrown once — almost always a node id left behind by
      // a Cavalry re-export. This exists because the failure mode was catastrophic and silent: the
      // whole tick body used to sit under ONE try, so the first bad id aborted the frame before
      // `player.tick()` ever ran and the visualizer froze on a static pose with nothing on screen
      // to say why. Retiring the offender keeps every other shape (and the tick itself) alive, and
      // logs the id once instead of 60x a second.
      const retired = new Set();

      const driveShape = (key, value) => {
        if (retired.has(key)) return;
        try {
          player.setAttribute(NODES[key], ATTR, value);
        } catch (e) {
          retired.add(key);
          console.error(`[CavalryViz] cannot drive ${NODES[key]}.${ATTR} ("${key}") — stale node id after a re-export? Retiring this shape.`, e);
        }
      };

      // Read per frame rather than captured, so a call site that swaps the ref in later is picked
      // up (same reason as the useEffect mirror above).
      const readLevel = () => {
        const amp = ampRef.current?.current;
        if (typeof amp !== 'number' || Number.isNaN(amp)) return null;
        return Math.min(Math.max(amp, 0) * VOICE_GAIN, 1);
      };

      // Smoothed 0-1 voice level per reactive state. Each decays to 0 whenever its state is not
      // active, so that oscillator eases back to its authored settings instead of freezing
      // mid-throw — and, just as importantly, so the state we just left stops moving.
      const levels = {};
      // Same retirement rule as `retired` above, for the non-blend nodes we drive.
      const oscRetired = new Set();
      REACTIVE_KEYS.forEach((key) => { levels[key] = 0; });

      // One-off wiring check, mirroring the blend check above. If a re-export ever connects one of
      // these attributes, tick() re-evaluates it every frame and silently wins over setAttribute —
      // the state would just stop reacting with nothing on screen to say why. `oscillator#23.maximum`
      // IS connected in the shipped file, which is why we drive its `minimum` instead.
      REACTIVE_KEYS.forEach((key) => {
        const { node, attrs } = REACTIVE[key];
        attrs.forEach(({ name }) => {
          try {
            if (player.getInConnection?.(node, name)) {
              console.warn(`[CavalryViz] ${node}.${name} is driven by a connection; tick() will override the manual "${key}" value.`);
            }
          } catch (_) { /* older player builds may not expose getInConnection */ }
        });
      });

      const tick = (ts) => {
        if (aborted) return;
        try {
          const requested = STATE_ALIASES[stateRef.current] ?? stateRef.current;
          const activeKey = STATE_KEYS.includes(requested) ? requested : FALLBACK_KEY;

          // In a voice-driven state the active shape's target follows the live level instead of
          // pinning at 1 — so the cluster breathes with whoever is talking, the way the legacy
          // Visualizer bars did, rather than holding one static pose for the whole turn.
          const activeLevel = REACTIVE[activeKey] ? readLevel() : null;
          let activeTarget = 1;
          let activeSpeed  = LERP_SPEED;
          if (activeLevel !== null) {
            const { floor } = REACTIVE[activeKey];
            activeTarget = floor + activeLevel * (1 - floor);
            activeSpeed  = activeTarget > blends[activeKey] ? VOICE_ATTACK : VOICE_RELEASE;
          }

          STATE_KEYS.forEach((key) => {
            const isActive = key === activeKey;
            const target = isActive ? activeTarget : 0;
            blends[key] += (target - blends[key]) * (isActive ? activeSpeed : LERP_SPEED);
            const { min, max } = ranges[key];
            driveShape(key, min + blends[key] * (max - min));
          });

          // Drive each state's own oscillator, not just the morph. This is what makes a voice state
          // read as reactive rather than as a loop that happens to be playing while someone talks.
          // Note these writes are only VISIBLE in proportion to their shape's blend (see the wiring
          // note at REACTIVE) — driving the inactive one is harmless but does nothing, which is
          // exactly why each state needs its own node.
          REACTIVE_KEYS.forEach((key) => {
            const raw = key === activeKey ? (activeLevel ?? 0) : 0;
            levels[key] += (raw - levels[key]) * VOICE_SMOOTHING;
            if (oscRetired.has(key)) return;
            const { node, attrs } = REACTIVE[key];
            try {
              attrs.forEach(({ name, quiet, loud }) => {
                player.setAttribute(node, name, quiet + levels[key] * (loud - quiet));
              });
            } catch (e) {
              oscRetired.add(key);
              console.error(`[CavalryViz] cannot drive ${node} ("${key}") — stale node id after a re-export? The cluster will still morph, but this state falls back to its authored loop.`, e);
            }
          });
        } catch (e) {
          console.error('[CavalryViz] tick error:', e);
        }
        // Outside the try above ON PURPOSE: whatever went wrong upstream, the scene must still
        // advance a frame. Anything that stops this call stops the animation dead.
        try {
          player.tick(surface, ts);
        } catch (e) {
          console.error('[CavalryViz] tick render error:', e);
        }
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }

    // The visualizer used to give up after a single failed init and render a dead error box for
    // the rest of the session. That was survivable while it only ever mounted a full intro film
    // after boot; a returning guest mounts it seconds after the loader, so a transient failure
    // (WASM still in flight, a surface bound while the boot canvas is still tearing down) now
    // lands squarely on the one attempt it gets. Retry a few times with backoff instead — the
    // singletons above reset themselves on failure, so a retry genuinely redoes the work.
    const MAX_ATTEMPTS = 4;
    const RETRY_BASE_MS = 400;

    async function init(attempt = 0) {
      try {
        const { Module, player } = await getPlayer();
        if (aborted) return; // this mount's canvas is already gone
        setDims(sharedDims);
        const surface = bindSurface(Module, player, canvas, sharedDims);
        bound = { Module, player, surface };
        startLoop(player, surface);
        setError(null);
      } catch (err) {
        if (aborted) return;
        if (attempt < MAX_ATTEMPTS - 1) {
          const delay = RETRY_BASE_MS * (attempt + 1);
          console.warn(`[CavalryViz] init attempt ${attempt + 1} failed, retrying in ${delay}ms:`, err);
          retryTimer = setTimeout(() => init(attempt + 1), delay);
          return;
        }
        console.error('[CavalryViz] init error (giving up):', err);
        setError(err.message);
      }
    }

    init();

    return () => {
      aborted = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (retryTimer) clearTimeout(retryTimer);
      if (bound) {
        releaseSurface({ ...bound, canvas, targetKey: '#canvas' });
        bound = null;
        // The cached scene's GPU resources belonged to the context just released, so a remount
        // (a desktop<->mobile composition swap) must re-parse rather than reuse dead handles.
        // Only dropped when we actually bound something — an aborted init has nothing to undo.
        playerPromise = null;
        sharedDims = null;
      }
    };
  }, []);

  // `maxWidth` is either a plain number (legacy px, e.g. 560) or a CSS length string
  // (e.g. `u(CLUSTER_W)` -> `calc(140 * var(--u))`) so reel-mode call sites can inherit the
  // same `--u` viewport scaling the rest of the footer chrome uses instead of a size fixed
  // in raw px — a raw px canvas rendered ~2x oversized on phones and ~35-60% oversized on
  // desktop relative to the authored 139.69/140 cluster slot it sits inside.
  const displayW = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
  const aspectRatio = dims ? dims.w / dims.h : 1;

  if (error) {
    return <div style={{ color: 'red', fontSize: 11, padding: 8 }}>Cavalry: {error}</div>;
  }

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: displayW,
        aspectRatio,
        display: 'block',
        // Call sites deliberately pass a width WIDER than the flex slot they sit in (the authored
        // dot field only occupies part of the 1920x1080 comp, so the canvas has to overhang for the
        // art to read at its designed size). Without this the flex parent would shrink the canvas
        // straight back down to the slot width and undo that.
        flexShrink: 0,
        mixBlendMode: blendMode,
        filter,
      }}
    />
  );
}
